import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getAllUnits,
  getUnitsByLocation,
  getUnitsByStatus,
  getUnitsFiltered,
  updateUnit,
} from "@/api";
import { Unit, UnitPayload, UnitRead } from "@types";
import { useInfiniteList, usePagination } from "@hooks";

export const UNIT_QK = ["units"];

export const useInfiniteUnits = () => {
  return useInfiniteList<Unit>({
    queryKey: UNIT_QK,
    fetchFn: async (page) => {
      const res = await getAllUnits(page);
      return res.data;
    },
  });
};

export const useInfiniteUnitsByLocation = (locationId: string) => {
  return useInfiniteList<Unit>({
    queryKey: [...UNIT_QK, "location", locationId],
    fetchFn: async (page) => {
      const res = await getUnitsByLocation(locationId, page, 10);
      return res.data;
    },
  });
};

export const useInfiniteUnitsByStatus = (status: string) => {
  return useInfiniteList<Unit>({
    queryKey: [...UNIT_QK, "status", status],
    fetchFn: async (page) => {
      const res = await getUnitsByStatus(status, page);
      return res.data;
    },
  });
};

export const useInfiniteUnitsFiltered = (filters: {
  status?: string | null;
  locationId?: string | null;
}) => {
  const status = filters.status || undefined;
  const locationId = filters.locationId || undefined;
  return useInfiniteList<Unit>({
    queryKey: [...UNIT_QK, "filtered", locationId ?? "", status ?? ""],
    fetchFn: async (page) => {
      const res = await getUnitsFiltered(page, { status, locationId });
      return res.data;
    },
  });
};

export function useAllUnits() {
  const { skip, setSkip } = usePagination();

  const {
    data: response,
    error,
    isLoading: loading,
    refetch,
  } = useQuery({
    queryKey: [UNIT_QK, skip],
    queryFn: () => getAllUnits(skip),
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });

  const { data = [], totalCount = 0, totalPages = 1 } = response?.data ?? {};

  return {
    data,
    total: totalCount,
    totalPages,
    loading,
    error,
    refetch,
    skip,
    setSkip,
  };
}

export function useAllUnitsByLocation(
  locationId: string | null,
  limit?: number,
) {
  const { skip, setSkip } = usePagination();

  const {
    data: response,
    error,
    isLoading: loading,
    refetch,
  } = useQuery({
    queryKey: ["unitsByLocation", locationId, skip, limit],
    queryFn: () => getUnitsByLocation(locationId!, skip, limit),
    enabled: !!locationId,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });

  const responseData = response?.data;

  const data = Array.isArray(responseData)
    ? responseData
    : (responseData?.data ?? []);

  const totalCount = Array.isArray(responseData)
    ? data.length
    : (responseData?.totalCount ?? 0);

  const totalPages = Array.isArray(responseData)
    ? 1
    : (responseData?.totalPages ?? 1);

  return {
    data,
    total: totalCount,
    totalPages,
    loading,
    error,
    refetch,
    skip,
    setSkip,
  };
}
export function useUpdateUnit() {
  const queryClient = useQueryClient();

  type UpdateArgs = { id: string; payload: UnitPayload };

  const { mutateAsync: triggerUpdateUnit, isPending: loading } = useMutation<
    UnitRead,
    Error,
    UpdateArgs
  >({
    mutationFn: ({ id, payload }) => updateUnit(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: UNIT_QK });
    },
  });

  return { triggerUpdateUnit, loading };
}
